/**
 * TagManager Component
 *
 * Handles all tag-related functionality for additional questions:
 * - Tag prefix creation and editing
 * - Tag prefix deletion
 * - Tag prefix modal management
 * - Tag visualization for option fields
 */

import { Pill } from '@HoudiniUI/Pill/Pill';
import { bs } from '@Styles/styles';
import { ICustomFormFieldTagSettings } from '@ViewModels';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@WebComponents/HoverCard';
import { TagIcon } from '@WebComponents/svgs/icons/TagIcon';
import { css } from 'aphrodite';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { InfoPopover } from '../../../../../../../admin/components/InfoPopover/InfoPopover';
import { TagPrefixModal } from '../../TagPrefixModal';
import { styleSheet } from '../styles';
import { TagManagerProps, TagPrefixModalState } from '../types';

/**
 * TagManager component for handling tag functionality on option fields
 */
export const TagManager: React.FC<TagManagerProps> = ({
	fieldId,
	fieldLabel,
	isOptionField,
	questionIndex,
	tagGenerationSettings,
	onTagPrefixChange,
	onTagPrefixDelete,
	disabled = false,
	onModalCreateTag,
	onModalEditTag,
	onValidationTrigger,
}) => {
	const formContext = useFormContext();
	const watch = formContext?.watch;
	const [tagPrefixModalState, setTagPrefixModalState] = React.useState<TagPrefixModalState>({
		isOpen: false,
		questionLabel: '',
		fieldId: '',
		isEdit: false,
	});

	// Helper functions for tag management
	const hasTagPrefix = React.useCallback((): boolean => {
		return (
			tagGenerationSettings?.customFormFieldTagSettings?.some((setting: ICustomFormFieldTagSettings) => {
				return setting.fieldId === fieldId && setting.shouldGenerateTag;
			}) || false
		);
	}, [fieldId, tagGenerationSettings?.customFormFieldTagSettings]);

	const getTagPrefix = React.useCallback((): string | null => {
		return (
			tagGenerationSettings?.customFormFieldTagSettings?.find((setting: ICustomFormFieldTagSettings) => {
				return setting.fieldId === fieldId && setting.shouldGenerateTag;
			})?.tagPrefix || null
		);
	}, [fieldId, tagGenerationSettings?.customFormFieldTagSettings]);

	// Modal management handlers
	const handleTagPrefixModalOpen = React.useCallback(
		async (questionLabel: string, isEditing: boolean) => {
			// If creating a new tag (not editing) and validation trigger is available, check if field is empty
			if (!isEditing && onValidationTrigger && watch) {
				// Get the current field value from the form
				const currentField = watch(`customForm.fields.${questionIndex}`);

				const isFieldEmpty = !currentField?.label?.trim();

				if (isFieldEmpty) {
					const isValid = await onValidationTrigger(questionIndex);
					// If validation fails, don't open the modal
					if (!isValid) {
						return;
					}
				}
			}

			setTagPrefixModalState({
				isOpen: true,
				questionLabel,
				fieldId,
				isEdit: isEditing,
			});
		},
		[fieldId, questionIndex, onValidationTrigger, watch]
	);

	const handleTagPrefixModalClose = React.useCallback(
		(result?: { fieldId: string; tagPrefix: string } | null, canceled?: boolean) => {
			if (!canceled && result) {
				const { tagPrefix: newTagPrefix } = result;
				onTagPrefixChange(fieldId, newTagPrefix);
			}
			setTagPrefixModalState(prev => ({ ...prev, isOpen: false, questionLabel: '' }));
		},
		[fieldId, onTagPrefixChange]
	);

	const handleDeleteTagPrefix = React.useCallback(() => {
		onTagPrefixDelete(fieldId);
		setTagPrefixModalState(prev => ({ ...prev, isOpen: false, questionLabel: '' }));
	}, [fieldId, onTagPrefixDelete]);

	const fieldHasTagPrefix = hasTagPrefix();
	const tagPrefix = getTagPrefix();

	// Expose modal handlers to parent component
	React.useEffect(() => {
		if (onModalCreateTag) {
			onModalCreateTag(() => handleTagPrefixModalOpen(fieldLabel, false));
		}
		if (onModalEditTag) {
			onModalEditTag(() => handleTagPrefixModalOpen(tagPrefix || fieldLabel, true));
		}
	}, [onModalCreateTag, onModalEditTag, handleTagPrefixModalOpen, fieldLabel, tagPrefix]);

	// Don't render anything if not an option field or disabled
	if (!isOptionField || disabled) {
		return null;
	}

	// If no existing tag prefix and no field label, render nothing
	if (!fieldHasTagPrefix && !fieldLabel) {
		return null;
	}

	return (
		<>
			{/* Tag Management UI */}
			{fieldHasTagPrefix && tagPrefix ? (
				// Edit existing tag prefix
				<div className={css(styleSheet.tagPrefixContainer)}>
					<Pill label={`${tagPrefix}`} variant='tag' color='teal' onRemove={handleDeleteTagPrefix} />
					<InfoPopover message='Deleting a tag prefix will not delete the associated tags.' side='right' />
				</div>
			) : // Create new tag prefix
			null}

			{/* Tag Prefix Modal */}
			<TagPrefixModal
				isOpen={tagPrefixModalState.isOpen}
				onRequestClose={handleTagPrefixModalClose}
				questionLabel={tagPrefixModalState.questionLabel}
				fieldId={tagPrefixModalState.fieldId}
				hasExistingPrefix={fieldHasTagPrefix}
			/>
		</>
	);
};

/**
 * TagVisualization component for showing tag preview on option items
 * Used within FormFieldWithOptions to show how tags will be generated
 */
export const TagVisualization: React.FC<{
	fieldId: string;
	optionLabel: string;
	tagGenerationSettings?: {
		tagOnCompletion: boolean;
		completionTag: string;
		customFormFieldTagSettings: ICustomFormFieldTagSettings[];
	};
}> = ({ fieldId, optionLabel, tagGenerationSettings }) => {
	const tagPrefix = React.useMemo((): string | null => {
		return (
			tagGenerationSettings?.customFormFieldTagSettings?.find((setting: ICustomFormFieldTagSettings) => {
				return setting.fieldId === fieldId && setting.shouldGenerateTag;
			})?.tagPrefix || null
		);
	}, [fieldId, tagGenerationSettings]);

	if (!tagPrefix) {
		return null;
	}

	return (
		<div className={css(bs.absolute, styleSheet.afterInputIcon)}>
			<HoverCard openDelay={0} closeDelay={0}>
				<HoverCardTrigger>
					<TagIcon />
				</HoverCardTrigger>
				<HoverCardContent side='right' align='start'>
					<Pill label={`${tagPrefix}: ${optionLabel}`} variant='tag' color='teal' />
				</HoverCardContent>
			</HoverCard>
		</div>
	);
};
